'use client';

// Bridge for the Android/Chromium `beforeinstallprompt` event.
//
// That event fires ONCE per page load — as soon as the manifest + service
// worker + icons satisfy Chrome's installability heuristic, typically right
// after first paint — and it does NOT replay if missed. It also fires app-wide,
// including on the unauthenticated login route, well before the authenticated
// shell (and therefore any React component that would listen for it) mounts.
//
// So the actual capture happens in a tiny inline <script> in the root layout
// <head> (`installCaptureScript` in app/layout.tsx), which runs on every route
// before hydration, calls preventDefault() (we always surface our own install
// UI instead of the browser mini-infobar), stashes the event on `window`, and
// dispatches `lvdr:install-change`. This module is the React-facing side: it
// reads those globals and re-emits to `useSyncExternalStore` subscribers. It
// also attaches its own listeners as a fallback in case the inline script was
// ever blocked — both paths set the same globals, so they compose idempotently.

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

declare global {
  interface Window {
    __lvdrInstallPromptEvent?: BeforeInstallPromptEvent | null;
    __lvdrAppInstalled?: boolean;
  }
}

const CHANGE_EVENT = 'lvdr:install-change';
const listeners = new Set<() => void>();

function emit(): void {
  for (const cb of listeners) cb();
}

if (typeof window !== 'undefined') {
  // Primary signal: the inline capture script re-dispatches this on every change.
  window.addEventListener(CHANGE_EVENT, emit);
  // Fallback capture (idempotent with the inline script — same globals, and
  // preventDefault is safe to call more than once on the same event).
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    window.__lvdrInstallPromptEvent = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    window.__lvdrInstallPromptEvent = null;
    window.__lvdrAppInstalled = true;
    emit();
  });
}

/** Subscribe to install-state changes. Returns an unsubscribe fn. */
export function subscribeInstallStore(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/**
 * Primitive snapshot for `useSyncExternalStore` — MUST be a stable primitive
 * (never a fresh object) or React re-render-loops. `getServerSnapshot` returns
 * `'none'` so the static-export prerender pass never touches a browser API.
 */
export function getInstallSnapshot(): 'installed' | 'android' | 'none' {
  if (typeof window === 'undefined') return 'none';
  if (window.__lvdrAppInstalled === true) return 'installed';
  if (window.__lvdrInstallPromptEvent) return 'android';
  return 'none';
}

/** The captured, still-usable install event, or null once consumed/absent. */
export function getDeferredPrompt(): BeforeInstallPromptEvent | null {
  if (typeof window === 'undefined') return null;
  return window.__lvdrInstallPromptEvent ?? null;
}

/** Clear the captured event after `prompt()` (it is single-use per spec). */
export function clearDeferredPrompt(): void {
  if (typeof window !== 'undefined') window.__lvdrInstallPromptEvent = null;
  emit();
}
